/* theme.vat-apply.js
   - applyVatModeToNode(rootEl, mode)
   - applyVatModeToAll(mode)   -> applies to document
   - auto-observe containers (config below)
   - listen for 'vat:changed' or 'vat:apply' events for manual triggers
*/
// ---- Feature flag guard: if VAT toggle is disabled make vatHelpers a safe no-op and stop ----
(function(){
  // prefer body data attribute, then window.themeSettings, then default to enabled
  const body = typeof document !== 'undefined' ? (document.body || document.documentElement) : null;
  const attr = body && body.getAttribute ? body.getAttribute('data-enable-vat-toggle') : null;
  const fromBody = attr === 'true' ? true : (attr === 'false' ? false : null);

  const fromWindow = (typeof window !== 'undefined' && window.themeSettings && typeof window.themeSettings.enableVatToggle !== 'undefined')
    ? !!window.themeSettings.enableVatToggle
    : null;

  const VAT_ENABLED = fromBody !== null ? fromBody : (fromWindow !== null ? fromWindow : true);

  if (!VAT_ENABLED) {
    // create safe stub so other scripts can call vatHelpers without throwing
    window.vatHelpers = window.vatHelpers || {
      applyVatModeToNode: function(){ /* VAT disabled */ },
      applyVatModeToAll: function(){ /* VAT disabled */ },
      applyModeToPriceEl: function(){ /* VAT disabled */ }
    };
    // notify any listeners (optional)
    try { document.dispatchEvent(new CustomEvent('vat:disabled')); } catch(e){}
    // do not execute the rest of the file
    return;
  }
})();

(function () {
  const COOKIE_NAME = 'vat_display'; // must match the cookie used earlier ('vat_display')
  const INC = 'inc';
  const EXC = 'exc';

  // cookie helper (same as earlier file)
  function getCookie(name) {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for(let i=0;i<ca.length;i++){
      let c = ca[i].trim();
      if (c.indexOf(nameEQ) === 0) return decodeURIComponent(c.substring(nameEQ.length,c.length));
    }
    return null;
  }

  // Apply mode to a single .f-price element
  function applyModeToPriceEl(el, mode) {
    if (!el) return;
    const incSpans = el.querySelectorAll('.f-price--inc');
    const excSpans = el.querySelectorAll('.f-price--exc');

    if (mode === INC) {
      incSpans.forEach(s => s.style.display = '');
      excSpans.forEach(s => s.style.display = 'none');
    } else {
      incSpans.forEach(s => s.style.display = 'none');
      excSpans.forEach(s => s.style.display = '');
    }

    const unitInc = el.querySelector('[data-unit-price-inc]');
    const unitExc = el.querySelector('[data-unit-price-exc]');
    if (unitInc && unitExc) {
      if (mode === INC) { unitInc.style.display = ''; unitExc.style.display = 'none'; }
      else { unitInc.style.display = 'none'; unitExc.style.display = ''; }
    }

    const labelInc = el.querySelector('.vat-label--inc');
    const labelExc = el.querySelector('.vat-label--exc');
    if (labelInc && labelExc) {
      if (mode === INC) { labelInc.style.display = ''; labelExc.style.display = 'none'; }
      else { labelInc.style.display = 'none'; labelExc.style.display = ''; }
    }
  }

  // Apply mode across a root (container)
  function applyVatModeToNode(rootEl, mode) {
    if (!rootEl) return;
    // If root itself is a price element, apply directly
    if (rootEl.classList && rootEl.classList.contains('f-price')) {
      applyModeToPriceEl(rootEl, mode);
      return;
    }
    // otherwise find all f-price descendants
    const prices = rootEl.querySelectorAll ? rootEl.querySelectorAll('.f-price') : [];
    prices.forEach(el => applyModeToPriceEl(el, mode));
  }

  // Apply to whole document
  function applyVatModeToAll(mode) {
    mode = mode || getCookie(COOKIE_NAME) || EXC;
    const all = document.querySelectorAll('.f-price');
    all.forEach(el => applyModeToPriceEl(el, mode));
    // update header toggle UI (if present)
    const toggle = document.getElementById('vat-toggle');
    const btn = document.getElementById('vat-toggle-btn');
    if (toggle) toggle.setAttribute('data-state', mode);
    if (toggle) toggle.setAttribute('aria-checked', mode === INC ? 'true' : 'false');
    if (btn) btn.setAttribute('aria-pressed', mode === INC ? 'true' : 'false');
  }

  // A MutationObserver that watches specified containers and applies VAT to newly added nodes
  function watchContainers(selectors) {
    if (!selectors || !selectors.length) return;

    const observer = new MutationObserver((mutations) => {
      // batch nodes to avoid heavy DOM calls
      const roots = new Set();
      for (const m of mutations) {
        if (m.addedNodes && m.addedNodes.length) {
          m.addedNodes.forEach(node => {
            if (!(node instanceof HTMLElement)) return;
            // if node itself contains .f-price or is .f-price, handle
            if (node.matches && node.matches('.f-price')) roots.add(node);
            else if (node.querySelector && node.querySelector('.f-price')) roots.add(node);
            else {
              // sometimes the price is deeper; still add node to handle descendants later
              roots.add(node);
            }
          });
        }
      }
      if (roots.size === 0) return;
      const mode = getCookie(COOKIE_NAME) || EXC;
      roots.forEach(r => applyVatModeToNode(r, mode));
    });

    selectors.forEach(sel => {
      const el = document.querySelector(sel);
      if (!el) return;
      observer.observe(el, { childList: true, subtree: true });
    });

    // return observer so it can be disconnected if needed
    return observer;
  }

  // Public API: expose to window for manual calls
  window.vatHelpers = {
    applyVatModeToNode,
    applyVatModeToAll,
    applyModeToPriceEl
  };

  // Auto-init on DOM load
  document.addEventListener('DOMContentLoaded', () => {
    // 1) apply to the entire document initially (cookie-aware)
    applyVatModeToAll();

    // 2) Observe common dynamic containers — adjust selectors to match your theme:
    //    - product tab container(s)
    //    - search results container
    //    - recommended / related products containers
    // Change these selectors to your theme's actual container IDs/classes.
    const selectorsToWatch = [
      '#ProductTabs',          // example product tabs wrapper
      '.product-tabs',         // alternate
      '#SearchResults',        // search results container
      '.search-results',       // alternate
      '.recommendations',      // product recommendations
      '.ajax-product-list'     // any AJAX lists
    ];
    watchContainers(selectorsToWatch);

    // 3) Listen to custom events so other scripts can notify us after they inject HTML
    document.addEventListener('vat:apply', function(e) {
      // e.detail can contain { root: element } or { selector: '...' }
      const detail = (e && e.detail) || {};
      if (detail.root) applyVatModeToNode(detail.root, getCookie(COOKIE_NAME) || EXC);
      else if (detail.selector) {
        const node = document.querySelector(detail.selector);
        if (node) applyVatModeToNode(node, getCookie(COOKIE_NAME) || EXC);
      } else {
        applyVatModeToAll(getCookie(COOKIE_NAME) || EXC);
      }
    });

    // also respond to the VAT toggle change event you already dispatch (vat:changed)
    document.addEventListener('vat:changed', function(e) {
      const mode = (e && e.detail && e.detail.mode) || getCookie(COOKIE_NAME) || EXC;
      applyVatModeToAll(mode);
    });
  });
})();
